import {Box} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {Ago} from './row/Ago'
import {LABELS} from '../constants'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ReferencedEvent$key} from './__generated__/ReferencedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {ReferencedEventInner} from './ReferencedEventInner'
import {CommitIcon} from '@primer/octicons-react'

type ReferencedEventProps = {
  queryRef: ReferencedEvent$key
  rollupGroup?: Record<string, ReferencedEvent$key[]>
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  viewerLogin: string
}

export const ReferencedEventFragment = graphql`
  fragment ReferencedEvent on ReferencedEvent {
    databaseId
    willCloseSubject
    subject {
      __typename
    }
    actor {
      ...EventActor
    }
    commit {
      ...ReferencedEventInner
    }
    createdAt
  }
`

export function ReferencedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  viewerLogin,
}: ReferencedEventProps): JSX.Element {
  const {actor, createdAt, commit, databaseId, subject, willCloseSubject} = useFragment(
    ReferencedEventFragment,
    queryRef,
  )

  if (commit === null) {
    return <></>
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup && rollupGroup['ReferencedEvent'] ? rollupGroup['ReferencedEvent'] : [queryRef]
  const numItems = rolledUpGroup.length

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={issueUrl}
      showAgoTimestamp={false}
      leadingIconOnTop={true}
      leadingIcon={CommitIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.addedCommitsThatReferences(numItems)} `}
        <Ago timestamp={new Date(createdAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2, ml: [0, 0, 4, 4]}}>
          {rolledUpGroup.map((itemQueryRef, index) => (
            <ReferencedEventInner
              commitKey={commit}
              key={`${databaseId}_${index}`}
              subjectType={subject.__typename}
              viewerLogin={viewerLogin}
              willCloseSubject={willCloseSubject}
            />
          ))}
        </Box>
      </TimelineRow.Main>
    </TimelineRow>
  )
}

try{ ReferencedEvent.displayName ||= 'ReferencedEvent' } catch {}